import { gql } from "@apollo/client";
import { Form, Formik, FormikHelpers, FieldArray } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as yup from "yup";

import {
  Card,
  CardHeader,
  CardContent,
  FormGroup,
  FormControlLabel,
  makeStyles,
  MenuItem,
  Checkbox,
  IconButton,
  Button,
  Divider
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { Alert } from "@material-ui/lab";

import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import FormikCheckboxField from "src/components/form/FormikCheckboxField";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import Select from "src/components/form/Select";
import {
  campaignEditSidebar,
  campaignEditSidebarVariables
} from "src/graphql/generated/campaignEditSidebar";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { CalleeField } from "src/graphql/generated/globalTypes";
import useMutation from "src/util/useMutation";

import { CAMPAIGN_WITH_CALLEE_FIELDS } from "../../graphql/fragments";
import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import { FormikSaveButtons } from "./SaveButtons";

const useStyles = makeStyles(theme => ({
  sectionHeading: {
    marginBottom: theme.spacing(1)
  },
  addSectionButtonWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  saveButtonsWrapper: {
    marginTop: theme.spacing(3)
  },
  calleeAdditionalInfoHelp: {
    marginTop: theme.spacing(1),
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  calleeAdditionalInfoWrapper: {
    marginLeft: theme.spacing(6),
    marginBottom: theme.spacing(2)
  },
  calleeAddtionalInfoField: {
    marginLeft: "4.5px",
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem"
    },
    "& .MuiCheckbox-root": {
      padding: theme.spacing(1) / 2
    }
  },
  newCustomFieldForm: {
    paddingTop: theme.spacing(1),
    display: "flex"
  },
  talkingPointWrapper: {
    marginLeft: theme.spacing(6)
  },
  sectionCard: {
    marginTop: theme.spacing(2)
  },
  select: {
    minWidth: "20rem",
    marginRight: theme.spacing(1)
  }
}));

type TalkingPoints = {
  heading: string;
  text: string;
};

type CalleeAdditionalInfo = {
  name: boolean;
  phoneNumber: boolean;
  homeAddress: boolean;
  pollingPlace: boolean;
};

const calleeAdditionalInfoFields = [
  "name",
  "phoneNumber",
  "homeAddress",
  "pollingPlace"
] as const;

type Values = {
  enableTalkingPoints: boolean;
  talkingPoints: TalkingPoints[];
  enableCalleeAdditionalInfo: boolean;
  calleeAdditionalInfo: CalleeAdditionalInfo;
  sortedSidebarFieldIds: string[];
  goToNext?: boolean;
};

const schema = yup.object().shape({
  enableTalkingPoints: yup.boolean().required("Required"),
  talkingPoints: yup
    .array()
    .of(
      yup.object().shape({
        heading: yup.string().trim().required("Required"),
        text: yup.string().trim().required("Required")
      })
    )
    .test(
      "at-least-one-talking-point-group",
      "At least one set of talking points is required if enabled",
      (value, context) =>
        !!(!context.parent.enableTalkingPoints || (value && value.length > 0))
    ),
  enableCalleeAdditionalInfo: yup.boolean().required("Required"),
  calleeAdditionalInfo: yup.object().shape({
    name: yup.boolean().required("Required"),
    phoneNumber: yup.boolean().required("Required"),
    homeAddress: yup.boolean().required("Required"),
    pollingPlace: yup.boolean().required("Required")
  }),
  sortedSidebarFieldIds: yup.array().of(yup.string())
});

const EDIT_CAMPAIGN_SIDEBAR = gql`
  ${CAMPAIGN_WITH_CALLEE_FIELDS}

  mutation campaignEditSidebar(
    $campaignId: ID!
    $enableTalkingPoints: Boolean!
    $talkingPoints: [TalkingPointsInput!]!
    $enableCalleeAdditionalInfo: Boolean!
    $info: CalleeAdditionalInfoInput!
    $fieldIds: [ID!]!
  ) {
    campaignEditTalkingPoints(
      id: $campaignId
      enabled: $enableTalkingPoints
      talkingPoints: $talkingPoints
    ) {
      id
      enableTalkingPoints
      talkingPoints {
        heading
        text
      }
    }

    campaignEditCalleeAdditionalInfo(
      id: $campaignId
      enabled: $enableCalleeAdditionalInfo
      info: $info
    ) {
      id
      enableCalleeAdditionalInfo
      calleeAdditionalInfo {
        name
        phoneNumber
        homeAddress
        pollingPlace
      }
    }

    campaignUpdateSidebarOrder(
      campaignId: $campaignId
      sidebarFieldIds: $fieldIds
    ) {
      id
      ...CampaignWithCalleeFields
    }
  }
`;

export default function CallerInterface({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [editSidebar] = useMutation<
    campaignEditSidebar,
    campaignEditSidebarVariables
  >(EDIT_CAMPAIGN_SIDEBAR, {
    onErrorSnackbarMessage: "Could not edit caller interface",
    refetchQueries: ["getCampaign"],
    awaitRefetchQueries: true
  });

  const sendEditEvent = useCampaignEditEvents();

  const [displayNewCustomFieldForm, setDisplayNewCustomFieldForm] =
    useState(false);

  const customFieldCompatable = (field: {
    id: string | null;
    builtInField: CalleeField | null;
    sidebarOrder: number | null;
    dataSourceFieldName: string | null;
  }): boolean =>
    !!field.id &&
    (!field.builtInField ||
      // Allow the default email field if it's mapped
      (field.builtInField === CalleeField.EMAIL &&
        !!field.dataSourceFieldName));
  const campaignSidebarFieldIds = campaign.calleeFields
    .filter(customFieldCompatable)
    .map(f => f!.id) as string[];
  const sortCalleeFieldsBySidebarOrder = (
    fields: {
      id: string | null;
      builtInField: CalleeField | null;
      sidebarOrder: number | null;
      dataSourceFieldName: string | null;
    }[]
  ) =>
    _(fields)
      .filter(f => customFieldCompatable(f) && _.isInteger(f.sidebarOrder))
      .sortBy(f => f.sidebarOrder || 0)
      .map(f => f!.id)
      .value() as string[];
  const selectedCustomCalleeFieldsIds = sortCalleeFieldsBySidebarOrder(
    campaign.calleeFields
  );

  const fieldNameForId = (fieldId: string) =>
    campaign.calleeFields.find(f => f.id === fieldId)?.name;

  const handleSave = async (
    {
      enableTalkingPoints,
      talkingPoints,
      enableCalleeAdditionalInfo,
      calleeAdditionalInfo,
      sortedSidebarFieldIds,
      goToNext
    }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    sendEditEvent();

    const { data, errors } = await editSidebar({
      variables: {
        campaignId: campaign.id,
        enableTalkingPoints,
        talkingPoints: talkingPoints.map(({ heading, text }) => ({
          heading,
          text
        })),
        enableCalleeAdditionalInfo,
        info: _.pick(
          calleeAdditionalInfo,
          calleeAdditionalInfoFields
        ) as CalleeAdditionalInfo,
        fieldIds: sortedSidebarFieldIds
      }
    });
    setSubmitting(false);
    if (!errors) {
      resetForm({
        values: {
          enableTalkingPoints:
            data!.campaignEditTalkingPoints.enableTalkingPoints,
          talkingPoints: data!.campaignEditTalkingPoints.talkingPoints,
          enableCalleeAdditionalInfo:
            data!.campaignEditCalleeAdditionalInfo.enableCalleeAdditionalInfo,
          calleeAdditionalInfo:
            data!.campaignEditCalleeAdditionalInfo.calleeAdditionalInfo,
          sortedSidebarFieldIds: sortCalleeFieldsBySidebarOrder(
            data!.campaignUpdateSidebarOrder.calleeFields
          )
        }
      });
    }
    if (goToNext) {
      goToNextSection();
    } else {
      enqueueSnackbar("Saved caller sidebar", { variant: "info" });
    }
  };

  const fieldIsMapped = (builtInFieldId: CalleeField): boolean =>
    !!campaign.calleeFields.find(
      field => field.id && field.builtInField === builtInFieldId
    );

  return (
    <Formik
      initialValues={{
        enableTalkingPoints: campaign.enableTalkingPoints,
        talkingPoints: campaign.talkingPoints,
        enableCalleeAdditionalInfo: campaign.enableCalleeAdditionalInfo,
        calleeAdditionalInfo: campaign.calleeAdditionalInfo,
        sortedSidebarFieldIds: selectedCustomCalleeFieldsIds
      }}
      onSubmit={handleSave}
      validationSchema={schema}
    >
      {props => (
        <Form>
          <RouteLeavingGuard when={props.dirty} />
          <h2>Caller Sidebar</h2>
          <p>
            Select what extra infomation or context you want to display to the
            caller. The caller sidebar will appear alongside the call script
            thoughout the duration of the call.
          </p>
          <FormGroup>
            <h3 className={classes.sectionHeading}>Contact Information</h3>
            <FormikSwitchField
              name="enableCalleeAdditionalInfo"
              label="Enable contact information sidebar"
              disabled={props.isSubmitting}
            />
          </FormGroup>
          <div className={classes.calleeAdditionalInfoWrapper}>
            <div className={classes.calleeAdditionalInfoHelp}>
              Select the information about the contact you want to display to
              the caller
            </div>
            <div className={classes.calleeAddtionalInfoField}>
              <FormikCheckboxField
                label="Contact Name"
                name="calleeAdditionalInfo.name"
              />
            </div>
            <div className={classes.calleeAddtionalInfoField}>
              <FormikCheckboxField
                label="Contact Phone Number"
                name="calleeAdditionalInfo.phoneNumber"
              />
            </div>
            <div className={classes.calleeAddtionalInfoField}>
              <FormikCheckboxField
                label="Contact Home Address"
                name="calleeAdditionalInfo.homeAddress"
                checkboxProps={{ disabled: !fieldIsMapped(CalleeField.ZIP) }}
              />
            </div>
            <div className={classes.calleeAddtionalInfoField}>
              <FormikCheckboxField
                label="Contact Polling Place Address"
                name="calleeAdditionalInfo.pollingPlace"
                checkboxProps={{
                  disabled: !fieldIsMapped(
                    CalleeField.POLLING_PLACE_ADDRESS_LINE_1
                  )
                }}
              />
            </div>
            <FieldArray
              name="sortedSidebarFieldIds"
              render={arrayHelpers =>
                props.values.sortedSidebarFieldIds
                  .map((fieldId, index) => (
                    <div
                      key={fieldId}
                      className={classes.calleeAddtionalInfoField}
                    >
                      <FormControlLabel
                        label={fieldNameForId(fieldId)}
                        control={
                          <Checkbox
                            checked={true}
                            onChange={() => arrayHelpers.remove(index)}
                          />
                        }
                      />
                    </div>
                  ))
                  .concat(
                    <div className={classes.newCustomFieldForm}>
                      {!displayNewCustomFieldForm ? (
                        <Button
                          variant="contained"
                          onClick={() => setDisplayNewCustomFieldForm(true)}
                          disabled={
                            !campaignSidebarFieldIds.find(
                              id =>
                                !props.values.sortedSidebarFieldIds.includes(id)
                            )
                          }
                        >
                          + Add additional fields
                        </Button>
                      ) : (
                        <>
                          <Select
                            value=""
                            variant="underlined"
                            label="Select field"
                            className={classes.select}
                            onChange={({ target: { value } }) => {
                              arrayHelpers.push(value);
                              setDisplayNewCustomFieldForm(false);
                            }}
                          >
                            {campaignSidebarFieldIds
                              .filter(
                                id =>
                                  !props.values.sortedSidebarFieldIds.includes(
                                    id
                                  )
                              )
                              .map(fieldId => (
                                <MenuItem key={fieldId} value={fieldId}>
                                  {fieldNameForId(fieldId)}
                                </MenuItem>
                              ))}
                          </Select>
                          <Button
                            onClick={() => setDisplayNewCustomFieldForm(false)}
                            variant="contained"
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  )
              }
            />
          </div>

          <FormGroup>
            <h3 className={classes.sectionHeading}>Additional Information</h3>
            {props.errors.talkingPoints?.length &&
            props.touched.talkingPoints &&
            !props.values.talkingPoints?.length ? (
              <Alert severity="error">{props.errors.talkingPoints}</Alert>
            ) : null}
            <FormikSwitchField
              name="enableTalkingPoints"
              label="Enable customizable sidebar"
              disabled={props.isSubmitting}
            />
          </FormGroup>
          <div className={classes.talkingPointWrapper}>
            <p>
              This is a good place to list things like common talking points,
              key dates, or voter ID laws.
            </p>

            <FieldArray
              name="talkingPoints"
              render={arrayHelpers => (
                <div>
                  {props.values.talkingPoints.map((talkingPoints, index) => (
                    <Card className={classes.sectionCard}>
                      <CardHeader
                        title={`Section ${index + 1}`}
                        action={
                          <IconButton
                            aria-label="delete section"
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      />
                      <CardContent>
                        <FormGroup key={index}>
                          <FormikTextField
                            label="Subheading"
                            name={`talkingPoints[${index}].heading`}
                            size="small"
                          />

                          <FormikTextField
                            label="Custom text"
                            name={`talkingPoints[${index}].text`}
                            multiline
                          />
                        </FormGroup>
                      </CardContent>
                    </Card>
                  ))}

                  <div className={classes.addSectionButtonWrapper}>
                    <Button
                      variant="contained"
                      onClick={() =>
                        arrayHelpers.push({ heading: "", text: "" })
                      }
                    >
                      + Add a section
                    </Button>
                  </div>
                </div>
              )}
            ></FieldArray>
          </div>

          <Divider />
          <div className={classes.saveButtonsWrapper}>
            <FormikSaveButtons continueWithoutSaving={goToNextSection} />
          </div>
        </Form>
      )}
    </Formik>
  );
}
